import { skipToken, useMutation, useQuery } from '@tanstack/react-query'
import { queryKeys } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { retryUnlessClientError } from '../../lib/platform/errors'
import {
  platformBillingApi,
  platformOrganizationsApi,
  platformPlansApi,
  platformQuery,
  platformSettingsApi,
  platformSubscriptionsApi,
  platformTwoFactorApi,
} from '../../services/api/platform.api'
import type {
  AccessibilityGroupWrite,
  AccessibilityListParams,
  AccessibilityWrite,
  FeatureListParams,
  InvoiceListParams,
  PageParams,
  PaymentListParams,
  PlanCategoryWrite,
  PlanFeatureWrite,
  PlanListParams,
  PlanWrite,
  PlatformUserListParams,
  PlatformUserTwoFactorPatch,
  SettingChoiceListParams,
  SettingChoiceWrite,
  SettingListParams,
  SettingWrite,
  SubscriptionCreate,
  SubscriptionListParams,
  SubscriptionPatch,
  TwoFactorMode,
} from '../../app/modules/types/platform'

/**
 * No automatic retry on a 4xx anywhere in the console: 403 is the "superusers
 * only" page and 404 is not-found, so a retry only delays the screen that
 * explains it. Transport and 5xx failures keep one.
 */
const READ = { retry: retryUnlessClientError } as const

/** Invalidate every cached platform read of one resource family. */
const refresh = (...resources: string[]) =>
  Promise.all(
    resources.map((resource) => queryClient.invalidateQueries({ queryKey: ['platform', resource] })),
  )

/* ------------------------------------------------------------------ plans */

export function usePlatformPlans(params?: PlanListParams) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('plans', qs),
    queryFn: () => platformPlansApi.list(params),
    ...READ,
  })
}

export function usePlatformPlan(id: string | null) {
  return useQuery({
    queryKey: queryKeys.platform.detail('plans', id ?? ''),
    queryFn: id === null ? skipToken : () => platformPlansApi.get(id),
    ...READ,
  })
}

export function usePlanFormOptions() {
  return useQuery({
    queryKey: queryKeys.platform.options('plans'),
    queryFn: () => platformPlansApi.formOptions(),
    staleTime: 5 * 60_000,
    ...READ,
  })
}

export function useCreatePlan() {
  return useMutation({
    mutationFn: (payload: PlanWrite) => platformPlansApi.create(payload),
    onSuccess: () => refresh('plans'),
  })
}
export function useUpdatePlan() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<PlanWrite> }) =>
      platformPlansApi.update(id, payload),
    onSuccess: () => refresh('plans'),
  })
}
export function useDeletePlan() {
  return useMutation({
    mutationFn: (id: string) => platformPlansApi.remove(id),
    onSuccess: () => refresh('plans'),
  })
}

export function usePlanCategories(params?: PageParams, enabled = true) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('categories', qs),
    queryFn: () => platformPlansApi.listCategories(params),
    enabled,
    ...READ,
  })
}
export function useSaveCategory() {
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: PlanCategoryWrite }) =>
      id ? platformPlansApi.updateCategory(id, payload) : platformPlansApi.createCategory(payload),
    onSuccess: () => refresh('categories', 'plans'),
  })
}
export function useDeleteCategory() {
  return useMutation({
    mutationFn: (id: string) => platformPlansApi.removeCategory(id),
    onSuccess: () => refresh('categories', 'plans'),
  })
}

export function usePlanFeatures(params?: FeatureListParams, enabled = true) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('features', qs),
    queryFn: () => platformPlansApi.listFeatures(params),
    enabled,
    ...READ,
  })
}
export function useSaveFeature() {
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: PlanFeatureWrite }) =>
      id ? platformPlansApi.updateFeature(id, payload) : platformPlansApi.createFeature(payload),
    onSuccess: () => refresh('features', 'plans'),
  })
}
export function useDeleteFeature() {
  return useMutation({
    mutationFn: (id: string) => platformPlansApi.removeFeature(id),
    onSuccess: () => refresh('features', 'plans'),
  })
}

export function useAccessibilityGroups(params?: PageParams, enabled = true) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('accessibility-groups', qs),
    queryFn: () => platformPlansApi.listAccessibilityGroups(params),
    enabled,
    ...READ,
  })
}
export function useSaveAccessibilityGroup() {
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: AccessibilityGroupWrite }) =>
      id
        ? platformPlansApi.updateAccessibilityGroup(id, payload)
        : platformPlansApi.createAccessibilityGroup(payload),
    onSuccess: () => refresh('accessibility-groups', 'accessibilities'),
  })
}
export function useDeleteAccessibilityGroup() {
  return useMutation({
    mutationFn: (id: string) => platformPlansApi.removeAccessibilityGroup(id),
    onSuccess: () => refresh('accessibility-groups', 'accessibilities'),
  })
}

export function useAccessibilities(params?: AccessibilityListParams, enabled = true) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('accessibilities', qs),
    queryFn: () => platformPlansApi.listAccessibilities(params),
    enabled,
    ...READ,
  })
}
export function useSaveAccessibility() {
  return useMutation({
    mutationFn: ({ id, payload }: { id?: string; payload: AccessibilityWrite }) =>
      id ? platformPlansApi.updateAccessibility(id, payload) : platformPlansApi.createAccessibility(payload),
    onSuccess: () => refresh('accessibilities'),
  })
}
export function useDeleteAccessibility() {
  return useMutation({
    mutationFn: (id: string) => platformPlansApi.removeAccessibility(id),
    onSuccess: () => refresh('accessibilities'),
  })
}

/* ---------------------------------------------------------- subscriptions */

export function usePlatformSubscriptions(params?: SubscriptionListParams) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('subscriptions', qs),
    queryFn: () => platformSubscriptionsApi.list(params),
    ...READ,
  })
}
export function usePlatformSubscription(id: string | null) {
  return useQuery({
    queryKey: queryKeys.platform.detail('subscriptions', id ?? ''),
    queryFn: id === null ? skipToken : () => platformSubscriptionsApi.get(id),
    ...READ,
  })
}
export function useCreateSubscription() {
  return useMutation({
    mutationFn: (payload: SubscriptionCreate) => platformSubscriptionsApi.create(payload),
    onSuccess: () => refresh('subscriptions', 'organizations', 'plans'),
  })
}
export function useUpdateSubscription() {
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: SubscriptionPatch }) =>
      platformSubscriptionsApi.update(id, payload),
    onSuccess: () => refresh('subscriptions'),
  })
}
export function useChangeSubscriptionPlan() {
  return useMutation({
    mutationFn: ({ id, plan }: { id: string; plan: string }) => platformSubscriptionsApi.changePlan(id, plan),
    onSuccess: () => refresh('subscriptions', 'plans'),
  })
}
export function useCancelSubscription() {
  return useMutation({
    mutationFn: (id: string) => platformSubscriptionsApi.cancel(id),
    onSuccess: () => refresh('subscriptions', 'plans'),
  })
}

/** The organization picker: a search over every organization, only while it is open. */
export function usePlatformOrganizations(params: PageParams, enabled = true) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('organizations', qs),
    queryFn: () => platformOrganizationsApi.list(params),
    enabled,
    ...READ,
  })
}

/* ---------------------------------------------------------------- billing */

export function usePlatformInvoices(params?: InvoiceListParams) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('invoices', qs),
    queryFn: () => platformBillingApi.listInvoices(params),
    ...READ,
  })
}
export function usePlatformInvoice(id: string | null) {
  return useQuery({
    queryKey: queryKeys.platform.detail('invoices', id ?? ''),
    queryFn: id === null ? skipToken : () => platformBillingApi.getInvoice(id),
    ...READ,
  })
}
export function usePlatformPayments(params?: PaymentListParams) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('payments', qs),
    queryFn: () => platformBillingApi.listPayments(params),
    ...READ,
  })
}
export function usePlatformPayment(id: string | null) {
  return useQuery({
    queryKey: queryKeys.platform.detail('payments', id ?? ''),
    queryFn: id === null ? skipToken : () => platformBillingApi.getPayment(id),
    ...READ,
  })
}

/* --------------------------------------------------------------- settings */

export function usePlatformSettings(params?: SettingListParams) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('settings', qs),
    queryFn: () => platformSettingsApi.list(params),
    ...READ,
  })
}
export function useSettingFormOptions() {
  return useQuery({
    queryKey: queryKeys.platform.options('settings'),
    queryFn: () => platformSettingsApi.formOptions(),
    staleTime: 5 * 60_000,
    ...READ,
  })
}
export function useSettingHistory(id: number | null, params?: PageParams) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.history('settings', id ?? '', qs),
    queryFn: id === null ? skipToken : () => platformSettingsApi.history(id, params),
    ...READ,
  })
}
export function useSaveSetting() {
  return useMutation({
    mutationFn: ({ id, payload }: { id?: number; payload: SettingWrite }) =>
      id === undefined ? platformSettingsApi.create(payload) : platformSettingsApi.update(id, payload),
    onSuccess: () => refresh('settings'),
  })
}
export function useDeleteSetting() {
  return useMutation({
    mutationFn: (id: number) => platformSettingsApi.remove(id),
    onSuccess: () => refresh('settings'),
  })
}
export function useClearSettingsCache() {
  return useMutation({ mutationFn: (ids?: number[]) => platformSettingsApi.clearCache(ids) })
}

export function useSettingChoices(params?: SettingChoiceListParams, enabled = true) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('setting-choices', qs),
    queryFn: () => platformSettingsApi.listChoices(params),
    enabled,
    ...READ,
  })
}
export function useSaveSettingChoice() {
  return useMutation({
    mutationFn: ({ id, payload }: { id?: number; payload: SettingChoiceWrite }) =>
      id === undefined ? platformSettingsApi.createChoice(payload) : platformSettingsApi.updateChoice(id, payload),
    onSuccess: () => refresh('setting-choices', 'settings'),
  })
}
export function useDeleteSettingChoice() {
  return useMutation({
    mutationFn: (id: number) => platformSettingsApi.removeChoice(id),
    onSuccess: () => refresh('setting-choices', 'settings'),
  })
}

/* ------------------------------------------------- two-factor administration */

export function usePlatformTwoFactorMode() {
  return useQuery({
    queryKey: queryKeys.platform.twoFactorMode,
    queryFn: () => platformTwoFactorApi.getMode(),
    ...READ,
  })
}
export function useSetPlatformTwoFactorMode() {
  return useMutation({
    mutationFn: (mode: TwoFactorMode) => platformTwoFactorApi.setMode(mode),
    onSuccess: () => refresh('twoFactor', 'users'),
  })
}
export function usePlatformUsers(params?: PlatformUserListParams) {
  const qs = platformQuery(params)
  return useQuery({
    queryKey: queryKeys.platform.list('users', qs),
    queryFn: () => platformTwoFactorApi.listUsers(params),
    ...READ,
  })
}
export function usePlatformUserTwoFactor(id: string | null) {
  return useQuery({
    queryKey: queryKeys.platform.userTwoFactor(id ?? ''),
    queryFn: id === null ? skipToken : () => platformTwoFactorApi.getUser(id),
    ...READ,
  })
}
export function usePatchUserTwoFactor() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PlatformUserTwoFactorPatch }) =>
      platformTwoFactorApi.patchUser(id, data),
    onSuccess: () => refresh('users', 'twoFactor'),
  })
}
export function useResetUserTwoFactor() {
  return useMutation({
    mutationFn: (id: string) => platformTwoFactorApi.resetUser(id),
    onSuccess: () => refresh('users', 'twoFactor'),
  })
}
